/**
 * User test data builder
 * @module @orchard9ai/testing-utils/builders/user
 */

import { faker } from '@faker-js/faker';
import type { User, UserProfile, AuthenticatedUser, CreateUserInput } from '@orchard9ai/types';
import { UserRole, UserStatus } from '@orchard9ai/types';

/**
 * Base user builder
 */
export const userBuilder = () => {
  const userData = {
    id: faker.string.uuid(),
    email: faker.internet.email(),
    displayName: faker.person.fullName(),
    role: faker.helpers.arrayElement(Object.values(UserRole)),
    status: UserStatus.Active,
    createdAt: faker.date.past().toISOString(),
    updatedAt: faker.date.recent().toISOString(),
    profile: undefined as UserProfile | undefined,
  };

  return {
    withId(id: string) {
      userData.id = id;
      return this;
    },
    withEmail(email: string) {
      userData.email = email;
      return this;
    },
    withDisplayName(displayName: string) {
      userData.displayName = displayName;
      return this;
    },
    withRole(role: UserRole) {
      userData.role = role;
      return this;
    },
    withStatus(status: UserStatus) {
      userData.status = status;
      return this;
    },
    withProfile(profile: Partial<UserProfile>) {
      userData.profile = {
        userId: userData.id,
        firstName: faker.person.firstName(),
        lastName: faker.person.lastName(),
        avatarUrl: faker.image.avatar(),
        bio: faker.lorem.paragraph(),
        location: faker.location.city(),
        timezone: faker.location.timeZone(),
        language: faker.helpers.arrayElement(['en', 'es', 'fr', 'de']),
        ...profile,
      };
      return this;
    },
    asAdmin() {
      userData.role = UserRole.Admin;
      return this;
    },
    asGuest() {
      userData.role = UserRole.Guest;
      return this;
    },
    asInactive() {
      userData.status = UserStatus.Inactive;
      return this;
    },
    build(): User {
      const { profile, ...baseUser } = userData;
      return profile ? { ...baseUser, profile } : baseUser;
    },
  };
};

/**
 * Authenticated user builder
 */
export const authenticatedUserBuilder = () => {
  const baseUser = userBuilder().build();

  const authUser: AuthenticatedUser = {
    ...baseUser,
    accessToken: faker.string.alphanumeric(64),
    refreshToken: faker.string.alphanumeric(64),
    expiresIn: 3600,
    permissions: [],
  };

  return {
    withAccessToken(token: string) {
      authUser.accessToken = token;
      return this;
    },
    withRefreshToken(token: string) {
      authUser.refreshToken = token;
      return this;
    },
    withExpiresIn(seconds: number) {
      authUser.expiresIn = seconds;
      return this;
    },
    withPermissions(permissions: string[]) {
      authUser.permissions = permissions;
      return this;
    },
    withAdminPermissions() {
      authUser.permissions = [
        'users:read',
        'users:write',
        'users:delete',
        'admin:access',
        'settings:manage',
      ];
      return this;
    },
    build(): AuthenticatedUser {
      return { ...authUser };
    },
  };
};

/**
 * Create user input builder
 */
export const createUserInputBuilder = () => {
  const input: CreateUserInput = {
    email: faker.internet.email(),
    password: faker.internet.password({ length: 12 }),
    displayName: faker.person.fullName(),
  };

  return {
    withEmail(email: string) {
      input.email = email;
      return this;
    },
    withPassword(password: string) {
      input.password = password;
      return this;
    },
    withDisplayName(displayName: string) {
      input.displayName = displayName;
      return this;
    },
    withRole(role: UserRole) {
      input.role = role;
      return this;
    },
    build(): CreateUserInput {
      return { ...input };
    },
  };
};

/**
 * Create multiple users
 */
export const createUsers = (
  count: number,
  customizer?: (builder: ReturnType<typeof userBuilder>, index: number) => void
): User[] => {
  return Array.from({ length: count }, (_, index) => {
    const builder = userBuilder();
    if (customizer) {
      customizer(builder, index);
    }
    return builder.build();
  });
};
